import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate, Link as RouterLink } from 'react-router-dom';
import Layout from '../../../components/Layout';
import CategoryGroupPage from './categoryGroup/CategoryGroupPage';
import CategoryPage from './category/CategoryPage';
import SubCategoryPage from './subCategory/SubCategoryPage';
import CollectionPage from './collection/CollectionPage';
import SubCollectionPage from './subCollection/SubCollectionPage';
import { Box, Breadcrumbs, Typography, Link } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import CategoryIcon from '@mui/icons-material/Category';
import CollectionsIcon from '@mui/icons-material/Collections';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import RulerIcon from '@mui/icons-material/Straighten';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import SizePage from './size/SizePage';
import PurityPage from './purity/PurityPage';
import CaratagePage from './caratage/CaratagePage';
import ColorPage from './color/ColorPage';
import ProductTypePage from './productType/ProductTypePage';

const tabRoutes = [
  { label: 'Product Type', value: 'product-type', icon: CategoryIcon },
  { label: 'Category Group', value: 'category-group', icon: GroupWorkIcon },
  { label: 'Category', value: 'category', icon: CategoryIcon },
  { label: 'Sub-Category', value: 'sub-category', icon: CategoryIcon },
  { label: 'Collection', value: 'collection', icon: CollectionsIcon },
  { label: 'Sub Collection', value: 'sub-collection', icon: LibraryAddIcon },
  { label: 'Size', value: 'size', icon: RulerIcon },
  { label: 'Purity', value: 'purity', icon: WaterDropIcon },
  { label: 'Caratage', value: 'caratage', icon: WaterDropIcon },

  { label: 'Color', value: 'color', icon: ColorLensIcon },
];


const segmentLabels = {
  masters: 'Masters',
  styles: 'Styles',
  'product-atribute':'Product Attribute',
  'category-group': 'Category Group',
  'category':'Category',
  'sub-category': 'Sub-Category',
  'collection': 'Collection',
  'sub-collection': 'Sub Collection',
  'size': 'Size',
  'purity': 'Purity',
  'caratage': 'Caratage',
  'color':'Color',
};

const buildPath = (segments, idx) => '/' + segments.slice(0, idx + 1).join('/');

const CategoryGroupMasterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = tabRoutes.find(tab => location.pathname.split('/').pop() === tab.value)?.value || 'category-group';
  const handleChange = (event, newValue) => {
    if (newValue !== currentTab) {
      navigate(`/masters/styles/${newValue}`);
    }
  };

  // Scrollable tab bar logic
  const tabBarRef = useRef(null);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  // Update scrollByAmount to scroll by the visible width
  const scrollByAmount = tabBarRef.current ? tabBarRef.current.clientWidth : 850;
  const checkButtons = () => {
    const el = tabBarRef.current;
    if (!el) return;
    setShowPrev(el.scrollLeft > 0);
    setShowNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };
  useEffect(() => {
    checkButtons();
    const el = tabBarRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkButtons);
    window.addEventListener('resize', checkButtons);
    return () => {
      el.removeEventListener('scroll', checkButtons);
      window.removeEventListener('resize', checkButtons);
    };
  }, []);

  const TABS_VISIBLE = 6;
  const [tabWindowStart, setTabWindowStart] = useState(0);
  const atStart = tabWindowStart === 0;
  const atEnd = tabWindowStart + TABS_VISIBLE >= tabRoutes.length;

  const handlePrev = () => {
    if (!atStart) setTabWindowStart(tabWindowStart - 1);
  };
  const handleNext = () => {
    if (!atEnd) setTabWindowStart(tabWindowStart + 1);
  };

  const handleTabClick = (idx, value) => {
    handleChange(null, value);
  };

  // Always show breadcrumb: Masters > Styles > Category Group
  const breadcrumbItems = [
    { label: 'Masters', to: '/masters' },
    { label: 'Styles', to: '/masters/styles' },
    { label: segmentLabels[currentTab] || 'Product Type', to: currentTab },
  ];

  return (
    <Layout>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: '#00C853' }} />}
        aria-label="breadcrumb"
        sx={{ mb: 2, mt: 1 }}
      >
        {breadcrumbItems.map((item, idx) =>
          idx === breadcrumbItems.length - 1 ? (
            <Typography
              key={item.to}
              sx={{ color: '#111', fontWeight: 700, fontSize: 18, letterSpacing: 1, fontFamily: 'inherit' }}
            >
              {item.label}
            </Typography>
          ) : (
            <Link
              key={item.to}
              component={RouterLink}
              to={item.to}
              underline="hover"
              sx={{ color: '#111', fontWeight: 500, fontSize: 16, letterSpacing: 1, fontFamily: 'inherit', transition: 'color 0.3s ease', '&:hover': { color: '#111', fontWeight: 600 } }}
            >
              {item.label}
            </Link>
          )
        )}
      </Breadcrumbs>
      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative', marginBottom: 16 }}>
        <button
          onClick={handlePrev}
          disabled={atStart}
          style={{
            width: 25,
            height: 25,
            minWidth: 25,
            minHeight: 25,
            background: '#00C853',
            border: 'none',
            borderRadius: '10px', // Square with slight rounding
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            margin: '0 4px',
            opacity: atStart ? 0.5 : 1,
            cursor: atStart ? 'not-allowed' : 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}
          aria-label="Scroll left"
        >
          <ArrowBackIosNewIcon fontSize="small" color="inherit" />
        </button>
        <div
          style={{
            display: 'flex',
            overflow: 'hidden',
            flex: 1,
            borderBottom: '1.5px solid #e5e7eb',
            minHeight: 56,
            paddingBottom: 2,
          }}
        >
          {tabRoutes.slice(tabWindowStart, tabWindowStart + TABS_VISIBLE).map((tab, idx) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.value}
                onClick={() => handleTabClick(idx + tabWindowStart, tab.value)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 15,
                  minHeight: 56,
                  padding: '12px 24px',
                  marginRight: 5,
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  background: currentTab === tab.value ? '#E8F5E9' : 'transparent',
                  color: '#111',
                  fontWeight: currentTab === tab.value ? 700 : 500,
                  fontSize: 12,
                  letterSpacing: 0.5,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: 'none',
                  outline: 'none',
                  borderBottom: currentTab === tab.value ? '4px solid #00C853' : '4px solid transparent',
                }}
              >
                <span style={{ fontSize: 22, color: currentTab === tab.value ? '#00C853' : '#757575', marginRight: 30 }}><Icon /></span>
                {tab.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={handleNext}
          disabled={atEnd}
          style={{
            width: 25,
            height: 25,
            minWidth: 25,
            minHeight: 25,
            marginRight:25,
            background: '#00C853',
            border: 'none',
            borderRadius: '10px', // Square with slight rounding
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 16,
            margin: '0 4px',
            opacity: atEnd ? 0.5 : 1,
            cursor: atEnd ? 'not-allowed' : 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}
          aria-label="Scroll right"
        >
          <ArrowForwardIosIcon fontSize="small" color="inherit" />
        </button>
      </div>
      {/* Tab Content */}
      <Routes>
        <Route index element={<Navigate to="product-type" />} />
        <Route path="product-type" element={<ProductTypePage />} />
        <Route path="category-group" element={<CategoryGroupPage />} />
        <Route path="category" element={<CategoryPage />} />
        <Route path="sub-category" element={<SubCategoryPage />} />
        <Route path="collection" element={<CollectionPage />} />
        <Route path="sub-collection" element={<SubCollectionPage />} />
        <Route path="size" element={<SizePage />} />
        <Route path="purity" element={<PurityPage />} />
        <Route path="caratage" element={<CaratagePage />} />
        <Route path="color" element={<ColorPage />} />
        <Route path="*" element={<div>Not Found: {location.pathname}</div>} />
      </Routes>
    </Layout>
  );
};

export default CategoryGroupMasterPage; 